import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Divider,
  Image,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { getApiEndpoint } from "../../config/config";
import "./InfoVerificationPage.css";

const WelcomeSurveyPage = () => {
  const { clientcode } = useParams();

  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);
  const [submitError, setSubmitError] = useState("");

  // Fetch the company (name + logo) for this client code.
  useEffect(() => {
    let cancelled = false;

    const fetchCompany = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const response = await fetch(
          getApiEndpoint(
            `/v2/welcome-survey/company?code=${encodeURIComponent(clientcode)}`
          )
        );
        if (!response.ok) throw new Error("welcome-survey-unavailable");
        const data = await response.json();
        if (!cancelled) setCompany(data);
      } catch (err) {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCompany();
    return () => {
      cancelled = true;
    };
  }, [clientcode]);

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitError("");
    setSuccessInfo(null);

    if (!phone.trim() && !email.trim()) {
      setSubmitError("Please enter a phone number or email.");
      return;
    }

    setSubmitting(true);
    try {
      const companyId =
        company?.id || company?.company_id || company?.companyId;
      const response = await fetch(
        getApiEndpoint("/v1/welcome-survey/customer"),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phone: phone.trim(),
            email: email.trim(),
            companyId,
          }),
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          data.message || "Something went wrong. Please try again."
        );
      }
      const data = await response.json();
      setSuccessInfo(data);
      setPhone("");
      setEmail("");
    } catch (err) {
      setSubmitError(err.message || "Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="verify-page">
        <Paper className="verify-card" shadow="sm" radius="md" p="xl">
          <Stack align="center" gap="sm">
            <Loader />
            <Text c="dimmed">Loading…</Text>
          </Stack>
        </Paper>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="verify-page">
        <Paper className="verify-card" shadow="sm" radius="md" p="xl">
          <Stack align="center" gap="sm">
            <Title order={3} ta="center">
              No Welcome Survey Available
            </Title>
            <Text c="dimmed" ta="center">
              This company doesn't currently have a welcome survey set up.
              Please reach out to your manager for help.
            </Text>
          </Stack>
        </Paper>
      </div>
    );
  }

  const companyName = company?.name || company?.company_name || "";
  const logoUrl = company?.logo || company?.logo_url || company?.logoUrl || "";

  return (
    <div className="verify-page">
      <Paper className="verify-card" shadow="sm" radius="md" p="xl">
        <form onSubmit={handleSubmit}>
          <Stack align="center" gap="md">
            <Title order={2} ta="center">
              Customer Welcome Survey
            </Title>

            {logoUrl && (
              <Image
                src={logoUrl}
                alt={companyName || "Company logo"}
                fit="contain"
                h={100}
                w="auto"
                mah={100}
              />
            )}
            {companyName && (
              <Title order={2} ta="center">
                {companyName}
              </Title>
            )}

            <TextInput
              w="100%"
              mt="md"
              label="Phone number"
              type="tel"
              placeholder="(555) 555-5555"
              value={phone}
              onChange={e => setPhone(e.currentTarget.value)}
            />

            <Divider w="100%" label="or" labelPosition="center" />

            <TextInput
              w="100%"
              label="Email"
              type="email"
              placeholder="customer@example.com"
              value={email}
              onChange={e => setEmail(e.currentTarget.value)}
            />

            {successInfo && (
              <Alert color="green" w="100%" title="Response submitted">
                Customer ID {successInfo.customerId}
                {(successInfo.firstName || successInfo.lastName) &&
                  ` — ${[successInfo.firstName, successInfo.lastName]
                    .filter(Boolean)
                    .join(" ")}`}{" "}
                has been sent the welcome survey
                {successInfo.sentVia === "email"
                  ? " by email"
                  : successInfo.sentVia === "sms" ||
                      successInfo.sentVia === "text"
                    ? " by text message"
                    : successInfo.sentVia
                      ? ` by ${successInfo.sentVia}`
                      : ""}
                .
              </Alert>
            )}

            {submitError && (
              <Alert color="red" w="100%" title="Error">
                {submitError}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              mt="sm"
              loading={submitting}
              disabled={!phone.trim() && !email.trim()}
            >
              Submit
            </Button>
          </Stack>
        </form>
      </Paper>
    </div>
  );
};

export default WelcomeSurveyPage;
